import { useState } from 'react';

import { useQuery } from '@apollo/client';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Container, Offcanvas, Spinner } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroller';

import CardReview from './CardReview';
import ErrorPage from '@website/components/utilities/ErrorPage';
import {
  Trip,
  TripReviewsDocument,
  TripReviewsQuery,
  TripsPageQuery,
} from '@website/types/graphqlOperations';


/**
 * Reviews OffCanvas
 *
 * Used to show more reviews, in a way that's easy to scroll through on mobile.
 */
export function ReviewsOffCanvas({
  show,
  setShow,
  reviewsSummary,
  tripID,
  host,
}: {
  show: boolean;
  setShow: (show: boolean) => void;
  reviewsSummary: Trip['reviewsSummary'];
  tripID: string;
  host: TripsPageQuery['tripByURL']['host'];
}) {
  // Initial query for more reviews
  const { data, error, fetchMore } = useQuery(TripReviewsDocument, {
    variables: {
      id: tripID,
    },
  });

  /**
   * Fetch More Reviews Function
   */
  function fetchMoreReviews() {
    return fetchMore({
      variables: {
        reviewsNextToken: data.trip.reviews.nextToken,
      },
      updateQuery: (
        prev: TripReviewsQuery,
        { fetchMoreResult }: { fetchMoreResult: TripReviewsQuery },
      ): TripReviewsQuery => {
        if (!fetchMoreResult || !fetchMoreResult.trip.reviews.items) {
          return prev;
        }

        return {
          ...prev,
          trip: {
            ...prev.trip,
            reviews: {
              ...prev.trip.reviews,
              nextToken: fetchMoreResult.trip.reviews.nextToken,
              items: [
                ...prev.trip.reviews.items,
                ...fetchMoreResult.trip.reviews.items,
              ],
            },
          },
        };
      },
    });
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <Offcanvas
      show={show}
      placement="start"
      onHide={() => setShow(false)}
      style={{ minWidth: '90%' }}
    >
      <Offcanvas.Header closeButton className="container">
        <h3>
          <FontAwesomeIcon icon={faStar} />{' '}
          {reviewsSummary.fiveStarRatingAverage.toFixed(1)} -{' '}
          {reviewsSummary.reviewCount} Reviews
        </h3>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Container>
          {!data && <Spinner animation="grow" />}

          {data && (
            <InfiniteScroll
              pageStart={1}
              loadMore={fetchMoreReviews}
              hasMore={!!data.trip.reviews.nextToken}
              loader={<Spinner animation="grow" />}
              useWindow={false}
            >
              {data.trip.reviews.items.map((review) => (
                <CardReview
                  key={review.id}
                  review={review}
                  host={{
                    id: host.id,
                    name: host.name,
                  }}
                />
              ))}
            </InfiniteScroll>
          )}
        </Container>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

/**
 * Reviews
 */
export default function Reviews({
  reviews,
  reviewsSummary,
  tripID,
  host,
}: {
  reviews: TripsPageQuery['tripByURL']['reviews'];
  reviewsSummary: Trip['reviewsSummary'];
  tripID: string;
  host: TripsPageQuery['tripByURL']['host'];
}) {
  // State for showing the OffCanvas
  const [show, setShow] = useState<boolean>(false);

  return (
    <div>
      <h2 className="">
        Rated {reviewsSummary.fiveStarRatingAverage.toFixed(1)}
      </h2>

      <p className="subtitle text-sm text-primary">
        {reviewsSummary.reviewCount} verified review
        {reviewsSummary.reviewCount > 1 ? 's' : ''}
      </p>

      {reviews.items.map((review) => (
        <CardReview key={review.id} review={review} host={host} />
      ))}

      {reviewsSummary.reviewCount > 3 && (
        <Button className="mb-4 mt-1 ms-1" onClick={() => setShow(true)}>
          Show More Reviews
        </Button>
      )}

      {show && (
        <ReviewsOffCanvas
          reviewsSummary={reviewsSummary}
          show={show}
          setShow={setShow}
          tripID={tripID}
          host={host}
        />
      )}
    </div>
  );
}
